import {
  faCircleInfo,
  faCircleNotch,
  faEdit,
  faLayerGroup,
  faMerge,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper, Row } from "@tanstack/react-table";

import { Connection } from "src/api/connectApi/types";
import { startsWithInternalPrefix } from "src/api/constants";
import { BetaPill } from "src/base-components/BetaPill";
import { ColumnFilter } from "src/base-components/ColumnFilter";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill } from "src/base-components/Pill";
import { SortableColumnHeader } from "src/base-components/SortableColumnHeader";
import { Cell, Header } from "src/base-components/Table";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { isBetaConnection } from "src/connections/CreateConnectionGrid";
import { ProviderIcon } from "src/connections/config/Icon";
import {
  getConnectionType,
  getTestableStatus,
} from "src/connections/connectionStatus";
import { ConnectionActions } from "src/connections/ListActions";
import {
  ConnectionListRow,
  getRowCreatedAt,
  getRowIdValue,
  getRowNameValue,
  getRowTypeValue,
  getRowUpdatedAt,
  SDK_BUNDLE_TYPE_LABEL,
  SDK_SCRIPT_TYPE_LABEL,
} from "src/connections/sdk/rows";
import { SdkRowActions } from "src/connections/sdk/SdkRowActions";
import { SdkScriptPill } from "src/connections/sdk/SdkScriptPill";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { dateFromNow, formatDate } from "src/utils/datetime";

type ConnectionColumns = {
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onTest: (id: string) => void;
  testingConnection?: Connection;
  onEditSdkBundle: (bundle: ProviderSdkBundleResponse) => void;
  onDeleteSdkBundle: (bundle: ProviderSdkBundleResponse) => void;
  onAddScriptToBundle: (script: ProviderSdkConfigResponse) => void;
  onEditScript: (script: ProviderSdkConfigResponse) => void;
  onDeleteScript: (script: ProviderSdkConfigResponse) => void;
  typeFilter?: ConnectionTypeLabel | null;
  onTypeFilterChange?: (type: ConnectionTypeLabel | null) => void;
};

const ScriptIcon: React.FC<{ iconSrc?: string }> = ({ iconSrc }) =>
  iconSrc ? (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white">
      <img alt="" className="h-4 w-4 object-contain" src={iconSrc} />
    </span>
  ) : (
    <Icon icon={faLayerGroup} size="xs" />
  );

const ConnectionNameContent: React.FC<{ connection: Connection }> = ({
  connection,
}) => {
  const isInternalConnection = startsWithInternalPrefix(connection.name);
  return (
    <span className="inline-flex items-center gap-2">
      <ProviderIcon
        mediaKey={connection.configuration.media_key}
        provider={connection.provider}
        size="sm"
      />
      {isInternalConnection && (
        <Pill size="sm" variant="red">
          <Pill.Text>Internal</Pill.Text>
        </Pill>
      )}
      <span className="inline-block">{connection.name}</span>
      {connection.disable_credential_bg_jobs &&
        isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && (
          <Pill size="sm" variant="yellow">
            <Pill.Text>Disabled credentials refresh</Pill.Text>
          </Pill>
        )}
      {isBetaConnection(connection.provider) && <BetaPill placement="right" />}
    </span>
  );
};

const ScriptNameContent: React.FC<{ script: ProviderSdkConfigResponse }> = ({
  script,
}) => (
  <span className="inline-flex items-center gap-2">
    <ScriptIcon iconSrc={script.icon_path} />
    <span className="inline-block">{script.name}</span>
  </span>
);

// Bundle rows surface a single member-script pill; remaining scripts are
// summarised with a "+N more" indicator and shown in full inside the modal.
const BUNDLE_PILL_LIMIT = 1;

const BundleNameContent: React.FC<{ bundle: ProviderSdkBundleResponse }> = ({
  bundle,
}) => {
  const visibleScripts = bundle.configs.slice(0, BUNDLE_PILL_LIMIT);
  const hiddenCount = bundle.configs.length - visibleScripts.length;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white">
        <Icon icon={faMerge} size="xs" />
      </span>
      <span className="inline-block">{bundle.name}</span>
      <span className="flex items-center gap-1">
        {visibleScripts.map((script, index) => (
          <SdkScriptPill
            key={script.id}
            iconSrc={script.icon_path}
            // The "+N more" suffix belongs inside the last visible pill.
            moreCount={index === visibleScripts.length - 1 ? hiddenCount : 0}
            name={script.name}
          />
        ))}
      </span>
    </span>
  );
};

const SdkScriptRowActions: React.FC<{
  rowId: string;
  bundleNames: string[];
  onAddToBundle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ rowId, bundleNames, onAddToBundle, onEdit, onDelete }) => (
  <div className="invisible flex gap-2 group-hover:visible">
    <Tooltip
      body={
        bundleNames.length > 0
          ? `This SDK Script is part of the following bundles: ${bundleNames.join(", ")}`
          : undefined
      }
      placement="top"
      title="Add to SDK Bundle"
      asChild
    >
      <IconButton
        dataLoc={`add-script-to-bundle-${rowId}`}
        icon={faMerge}
        size="xs"
        onClick={(event) => {
          event.stopPropagation();
          onAddToBundle();
        }}
      />
    </Tooltip>
    <Tooltip placement="top" title="Edit SDK Script" asChild>
      <IconButton
        dataLoc={`edit-sdk-script-${rowId}`}
        icon={faEdit}
        size="xs"
        onClick={(event) => {
          event.stopPropagation();
          onEdit();
        }}
      />
    </Tooltip>
    <Tooltip placement="top" title="Delete SDK Script" asChild>
      <IconButton
        dataLoc={`delete-sdk-script-${rowId}`}
        icon={faTrashCan}
        size="xs"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
      />
    </Tooltip>
  </div>
);

export type ConnectionTypeLabel =
  | ReturnType<typeof getConnectionType>
  | typeof SDK_SCRIPT_TYPE_LABEL
  | typeof SDK_BUNDLE_TYPE_LABEL;

export const CONNECTION_TYPE_FILTER_OPTIONS = [
  "Integration",
  "Manifest Integration",
  "Inbound Webhook",
  "Custom Connection",
  "Database",
  SDK_SCRIPT_TYPE_LABEL,
  SDK_BUNDLE_TYPE_LABEL,
] as const satisfies readonly ConnectionTypeLabel[];

export const getConnectionTypeFilterOptions = (): {
  key: ConnectionTypeLabel;
  value: ConnectionTypeLabel;
}[] =>
  CONNECTION_TYPE_FILTER_OPTIONS.filter((type) => {
    if (type === "Manifest Integration") {
      return isFeatureFlagEnabled(FEATURE_FLAGS.powertools);
    }
    if (type === SDK_SCRIPT_TYPE_LABEL || type === SDK_BUNDLE_TYPE_LABEL) {
      return isFeatureFlagEnabled(FEATURE_FLAGS.webSdk);
    }
    return true;
  }).map((type) => ({ key: type, value: type }));

export const filterRowsByType = (
  rows: ConnectionListRow[],
  typeFilter: ConnectionTypeLabel | null,
): ConnectionListRow[] => {
  if (!typeFilter) return rows;
  return rows.filter((row) => getRowTypeValue(row) === typeFilter);
};

const isoDateSortingFn = (
  rowA: Row<ConnectionListRow>,
  rowB: Row<ConnectionListRow>,
  columnId: string,
): number => {
  const left = rowA.getValue<string | null | undefined>(columnId);
  const right = rowB.getValue<string | null | undefined>(columnId);

  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  return left.localeCompare(right);
};

const columnHelper = createColumnHelper<ConnectionListRow>();

export const columns = ({
  onDelete,
  onEdit,
  onTest,
  testingConnection,
  onEditSdkBundle,
  onDeleteSdkBundle,
  onAddScriptToBundle,
  onEditScript,
  onDeleteScript,
  typeFilter,
  onTypeFilterChange,
}: ConnectionColumns) => [
  columnHelper.accessor((row) => getRowIdValue(row), {
    id: "id",
    header: (info) => (
      <Header info={info}>
        <span className="pl-1">ID</span>
        <Tooltip placement="top" title="ID">
          <div className="ml-1">
            <Icon icon={faCircleInfo} size="2xs" />
          </div>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CopyTextIcon
          dataLoc={
            info.row.original.kind === "connection"
              ? "copy-connection-id"
              : "copy-sdk-id"
          }
          feedback="inline"
          tooltip="Copy ID"
          value={info.getValue()}
        />
      </Cell>
    ),
    minSize: 55,
    maxSize: 55,
    size: 55,
    filterFn: "includesString",
  }),
  columnHelper.accessor((row) => getRowNameValue(row), {
    id: "connections",
    enableSorting: true,
    sortingFn: "text",
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="connections-sort-name"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Connection name
        </SortableColumnHeader>
      </Header>
    ),
    filterFn: "includesString",
    cell: (info) => {
      const row = info.row.original;
      return (
        <Cell info={info}>
          {row.kind === "connection" && (
            <ConnectionNameContent connection={row.connection} />
          )}
          {row.kind === "sdkScript" && (
            <ScriptNameContent script={row.script} />
          )}
          {row.kind === "sdkBundle" && (
            <BundleNameContent bundle={row.bundle} />
          )}
        </Cell>
      );
    },
  }),
  columnHelper.accessor((row) => getRowUpdatedAt(row), {
    id: "updated",
    maxSize: 180,
    size: 180,
    enableSorting: true,
    sortingFn: isoDateSortingFn,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="connections-sort-last-updated"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Last updated
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => <Cell info={info}>{dateFromNow(info.getValue())}</Cell>,
  }),
  columnHelper.accessor((row) => getRowCreatedAt(row), {
    id: "created",
    maxSize: 212,
    size: 212,
    enableSorting: true,
    sortingFn: isoDateSortingFn,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="connections-sort-created-at"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Created at
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        {formatDate(info.getValue(), "MMM d, u 'at' h:mm aaa")}
      </Cell>
    ),
  }),
  ...(isFeatureFlagEnabled(FEATURE_FLAGS.powertools)
    ? [
        columnHelper.accessor(
          (row) =>
            row.kind === "connection"
              ? row.connection.credentials_refreshed_at
              : undefined,
          {
            id: "credentialsRefreshedAt",
            maxSize: 212,
            size: 212,
            header: (info) => (
              <Header info={info}>Credentials last refreshed at</Header>
            ),
            cell: (info) => {
              // Credentials only apply to connections; SDK script/bundle rows
              // have no refresh concept, so show a dash rather than "Never".
              if (info.row.original.kind !== "connection") {
                return <Cell info={info}>—</Cell>;
              }
              const refreshedAt = info.getValue();
              return (
                <Cell info={info}>
                  {refreshedAt
                    ? formatDate(refreshedAt, "MMM d, u 'at' h:mm aaa")
                    : "Never"}
                </Cell>
              );
            },
          },
        ),
      ]
    : []),
  columnHelper.accessor((row) => getRowTypeValue(row), {
    id: "type",
    maxSize: 180,
    size: 180,
    enableSorting: false,
    header: (info) => (
      <Header info={info}>
        <span className="inline-flex items-center gap-1">
          Type
          <ColumnFilter
            dataLoc="filter-connections-type"
            description="Filter by type:"
            elements={getConnectionTypeFilterOptions()}
            isFiltering={typeFilter != null}
            placement="bottom start"
            selected={typeFilter ?? undefined}
            showFooter={false}
            onResetRequest={() => onTypeFilterChange?.(null)}
            onSelect={(key) =>
              onTypeFilterChange?.(typeFilter === key ? null : key)
            }
          />
        </span>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Text>{info.getValue()}</Pill.Text>
        </Pill>
      </Cell>
    ),
  }),
  columnHelper.display({
    id: "actions",
    maxSize: 100,
    size: 100,
    header: (info) => <Header info={info} />,
    cell: (info) => {
      const row = info.row.original;

      if (row.kind === "connection") {
        const isTestingConnection = testingConnection?.id === row.connection.id;
        return (
          <Cell info={info}>
            {isTestingConnection ? (
              <span className="flex w-21 items-center">
                <div className="mr-1">
                  <Icon icon={faCircleNotch} spin={true} />
                </div>
                Testing...
              </span>
            ) : (
              <ConnectionActions
                rowId={row.connection.id}
                testableStatus={getTestableStatus(row.connection)}
                onDelete={() => onDelete(row.connection.id)}
                onEdit={() => onEdit(row.connection.id)}
                onTest={() => onTest(row.connection.id)}
              />
            )}
          </Cell>
        );
      }

      if (row.kind === "sdkBundle") {
        return (
          <Cell info={info}>
            <SdkRowActions
              entityLabel="SDK Bundle"
              rowId={row.bundle.id}
              onDelete={() => onDeleteSdkBundle(row.bundle)}
              onEdit={() => onEditSdkBundle(row.bundle)}
            />
          </Cell>
        );
      }

      return (
        <Cell info={info}>
          <SdkScriptRowActions
            bundleNames={row.bundleNames}
            rowId={row.script.id}
            onAddToBundle={() => onAddScriptToBundle(row.script)}
            onDelete={() => onDeleteScript(row.script)}
            onEdit={() => onEditScript(row.script)}
          />
        </Cell>
      );
    },
  }),
];
